import { Button } from 'base/ui/button';
import { Dialog } from 'base/ui/dialog';
import { RedErrorText, useSubmit } from 'base/ui/form';
import {
  IconChevronLeft,
  IconChevronRight,
  IconClear,
  IconOption,
} from 'base/ui/icons';
import { Inline, Rows, SpacerGrow } from 'base/ui/layout';
import { MenuButton } from 'base/ui/menu';
import { menuItem } from 'base/ui/menu_model';
import { DividerLine } from 'base/ui/panel';
import { Textarea } from 'base/ui/textarea';
import { ToastQueue } from 'base/ui/toast';
import { TextSmallLight } from 'base/ui/typography';
import { Comment, CommentThread } from 'blocks/blocks_service';
import { editorModeStore } from 'blocks/editor/editor_mode';
import { shortcut, useKeyboardShortcuts } from 'blocks/keyboard_shortcuts';
import { moveCommentThread } from 'blocks/sync/comments_store';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { SELECTION_HOVER_THROUGH_ATTR } from '../selection/frame';
import { globalSelectionsMeasurer } from '../selection/measurer';
import { Avatars } from './avatars';
import styles from './canvas.css';
import { FloatingCommentPanel } from './floating_panel';
import { CommentPin, threadDndDomain, ThreadDropTargetOpts } from './pin';
import {
  CommentContent,
  ThreadMenuButton,
  ThreadResolveButton,
  uniqueAvatars,
} from './ui';
import { CommentingUiStore, getAttachmentsAtCursor } from './ui_store';

export const CanvasCommentPins = observer(function _CanvasCommentPins({
  store,
  toastQueue,
}: {
  store: CommentingUiStore;
  toastQueue: ToastQueue;
}) {
  const { selectedThreadId } = store;
  const [selectedPin, setSelectedPin] = useState<HTMLDivElement | null>(null);

  useShowHideKeyboardShortcut(store, toastQueue);

  const unselect = React.useCallback(() => {
    store.selectedThreadId = undefined;
  }, [store]);

  const dropTargetOpts = React.useMemo(
    (): ThreadDropTargetOpts => ({
      mouseToDestination(clientX, clientY) {
        return { clientX, clientY };
      },
      processDrop: (
        threadId: number,
        pos: { clientX: number; clientY: number },
      ) => {
        const newAttach = getAttachmentsAtCursor(pos.clientX, pos.clientY);
        if (!newAttach) {
          toastQueue.push({ label: 'Cannot move the comment here' });
          return;
        }
        moveCommentThread(
          store.session.project,
          threadId.toString(),
          newAttach,
          store.activeViewport,
        );
      },
    }),
    [store, toastQueue],
  );

  if (!store.showComments) return null;
  return (
    <>
      {store.comments.allThreads.map((thread) =>
        selectedThreadId === thread.id || store.filterFn(thread) ? (
          <CanvasCommentPin
            pinRef={selectedThreadId === thread.id ? setSelectedPin : undefined}
            thread={thread}
            store={store}
            key={thread.id}
            dropTargetOpts={dropTargetOpts}
          />
        ) : (
          <React.Fragment key={thread.id} />
        ),
      )}
      {selectedThreadId && (
        <FloatingCommentPanel
          anchorTo={selectedPin}
          onClose={unselect}
          store={store}
        >
          <div className={styles.panel}>
            <SelectedThreadContent
              threadId={selectedThreadId}
              store={store}
              toastQueue={toastQueue}
            />
          </div>
        </FloatingCommentPanel>
      )}
      {threadDndDomain.state.get() && (
        <threadDndDomain.DropTarget
          opts={dropTargetOpts}
          className={styles.dropOverlay}
          {...{ [SELECTION_HOVER_THROUGH_ATTR]: true }}
        />
      )}
    </>
  );
});

const COMMENTS_TOAST_GROUP = Symbol('comments toast group');

function useShowHideKeyboardShortcut(
  store: CommentingUiStore,
  toastQueue: ToastQueue,
) {
  useKeyboardShortcuts(true, {
    [shortcut('c', { shift: true })]: React.useCallback(
      () => {
        if (store.showComments) {
          store.showCommentsPreference = false;
          // disable other features that might show comments
          store.addMode = false;
          store.selectedThreadId = undefined;
          toastQueue.push({
            label: 'Comments hidden',
            group: COMMENTS_TOAST_GROUP,
          });
        } else {
          store.showCommentsPreference = true;
          toastQueue.push({
            label: 'Comments visible',
            group: COMMENTS_TOAST_GROUP,
          });
        }
      }, // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [store],
    ),
  });
}

const CanvasCommentPin = observer(function _CanvasCommentPin({
  pinRef,
  thread,
  store,
  dropTargetOpts,
}: {
  pinRef?: React.Ref<HTMLDivElement>;
  thread: CommentThread;
  store: CommentingUiStore;
  dropTargetOpts: ThreadDropTargetOpts;
}) {
  const selection = store.comments.getThreadSelectionAndAnchoring(thread.id);
  const comments = store.comments.getCommentsInThread(thread.id);
  const avatars = uniqueAvatars(comments);

  const onClick = React.useCallback(() => {
    store.selectedThreadId =
      store.selectedThreadId === thread.id ? undefined : thread.id;
  }, [thread.id, store]);

  const onMouseEnter = React.useCallback(() => {
    store.canvasHoveringThreadId = thread.id;
  }, [store, thread.id]);
  const onMouseLeave = React.useCallback(() => {
    if (store.canvasHoveringThreadId === thread.id) {
      store.canvasHoveringThreadId = undefined;
    }
  }, [store, thread.id]);
  React.useEffect(() => onMouseLeave, [onMouseLeave]);

  if (
    // Thread has no anchor, so can't be rendered (likely deleted)
    selection == null ||
    selection.anchoring.viewport !== store.activeViewport ||
    comments.length === 0
  ) {
    return null;
  }

  const selMeasurer = globalSelectionsMeasurer.get(
    'selectable',
    selection.selectionId,
  );
  const box = selMeasurer.rect;
  if (box.width === 0 || box.height === 0) {
    // The selectable isn't mounted (e.g. it is in a different Space)
    return null;
  }
  const isSelected = thread.id === store.selectedThreadId;
  return (
    <CommentPin
      ref={pinRef}
      draggableSource={thread.id}
      dropTargetOpts={dropTargetOpts}
      avatars={avatars}
      comment={comments[0]}
      numReplies={comments.length - 1}
      isUnread={!store.comments.isThreadRead(thread.id)}
      isSelected={isSelected}
      isEnlarged={!isSelected && store.sidebarHoveringThreadId === thread.id}
      isFaded={!isSelected && thread.resolved}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      x={box.x + box.width * selection.anchoring.x}
      y={box.y + box.height * selection.anchoring.y}
    />
  );
});

const SelectedThreadContent = observer(function _SelectedThreadContent({
  threadId,
  store,
  toastQueue,
}: {
  threadId: number;
  store: CommentingUiStore;
  toastQueue: ToastQueue;
}) {
  const comments = store.comments.getCommentsInThread(threadId);
  const commentIds = React.useMemo(() => comments.map((c) => c.id), [comments]);

  React.useEffect(() => {
    for (const id of commentIds) {
      if (!store.comments.isCommentRead(id)) {
        store.comments.setCommentRead(id, true);
      }
    }
  }, [store, threadId, commentIds]);

  return (
    <Rows spacing={16}>
      <ThreadHeaderRow {...{ threadId, store, toastQueue }} />
      {comments.map((comment) => (
        <CommentRow comment={comment} key={comment.id} store={store} />
      ))}
      {store.canComment && (
        <>
          <DividerLine />
          <ReplyRow {...{ threadId, store }} />
        </>
      )}
    </Rows>
  );
});

const ThreadHeaderRow = observer(function _ThreadHeaderRow({
  threadId,
  store,
  toastQueue,
}: {
  threadId: number;
  store: CommentingUiStore;
  toastQueue: ToastQueue;
}) {
  const thread = store.comments.getThread(threadId);

  const onClose = React.useCallback(() => {
    store.selectedThreadId = undefined;
  }, [store]);

  const allThreads = store.comments.allThreads
    .filter(store.filterFn)
    .map((t) => t.id);
  const threadIndex = allThreads.indexOf(threadId);
  const goToPrevThread = React.useCallback(
    () => {
      store.goToCommentThread(allThreads[threadIndex - 1]);
    }, // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [threadIndex, store],
  );
  const goToNextThread = React.useCallback(
    () => {
      store.goToCommentThread(allThreads[threadIndex + 1]);
    }, // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [threadIndex, store],
  );

  return (
    <Inline spacing={8}>
      <Button
        icon={IconChevronLeft}
        tooltip="Previous comment"
        disabled={threadIndex <= 0}
        onClick={goToPrevThread}
        variant="tertiary"
        size="small"
      />
      <Button
        icon={IconChevronRight}
        tooltip="Next comment"
        disabled={threadIndex >= allThreads.length - 1}
        onClick={goToNextThread}
        variant="tertiary"
        size="small"
      />
      <SpacerGrow />
      {thread && <ThreadResolveButton {...{ thread, toastQueue, store }} />}
      {thread && <ThreadMenuButton {...{ thread, toastQueue, store }} />}
      <Button
        icon={IconClear}
        onClick={onClose}
        variant="tertiary"
        size="small"
      />
    </Inline>
  );
});

function CommentRow({
  comment,
  store,
}: {
  comment: Comment;
  store: CommentingUiStore;
}) {
  const [isEditing, setIsEditing] = React.useState(false);
  const [text, setText] = React.useState('');
  const hasText = text.trim().length > 0;

  const isMine = comment.author.id === store.comments.myAvatar.id;
  const showMenu = isMine && !isEditing;

  const [deleteMenuItem, deleteDialog, confirmDelete] = useDeleteCommentMenu(
    comment.id,
    store,
  );

  const startEditing = React.useCallback(() => {
    setIsEditing(true);
    setText(comment.content);
  }, [comment.content]);
  const cancelEditing = React.useCallback(() => {
    setIsEditing(false);
    setText('');
  }, []);
  const [loading, error, handleSubmit] = useSubmit(async () => {
    await store.comments.updateComment(comment.id, { content: text });
    setIsEditing(false);
  });

  return (
    <div className={classNames(styles.commentRow, showMenu && styles.hasMenu)}>
      <Avatars avatars={[comment.author]} max={1} />
      {isEditing ? (
        <Rows spacing={8}>
          <TextSmallLight>
            <Textarea
              allowNewlines={true}
              value={text}
              onChange={setText}
              placeholder="Edit comment"
              ref={focusOnMount}
            />
          </TextSmallLight>
          <RedErrorText error={error} />
          <Inline spacing={8}>
            <SpacerGrow />
            <Button
              label="Cancel"
              variant="secondary"
              onClick={cancelEditing}
            />
            <Button
              label={hasText ? 'Save' : 'Delete'}
              variant="primary"
              background={hasText ? undefined : 'negative'}
              onClick={hasText ? handleSubmit : confirmDelete}
              loading={loading}
            />
          </Inline>
        </Rows>
      ) : (
        <CommentContent comment={comment} />
      )}
      {showMenu && (
        <div className={styles.menuButton}>
          <MenuButton
            menu={[
              menuItem({ label: 'Edit', onClick: startEditing }),
              ...deleteMenuItem,
            ]}
            search="hidden"
            icon={IconOption}
            variant="tertiary"
            size="small"
          />
        </div>
      )}
      {deleteDialog}
    </div>
  );
}

function focusOnMount(el: HTMLTextAreaElement | null) {
  el?.focus();
}

function useDeleteCommentMenu(commentId: number, store: CommentingUiStore) {
  const [isConfirming, setIsConfirming] = React.useState(false);

  const canDelete = store.comments.canEditOrDeleteComment(commentId);

  const [loading, error, handleSubmit] = useSubmit(async () => {
    const res = await store.comments.updateComment(commentId, {
      deleted: true,
    });
    store.session.analytics.track({
      event: 'project:comment_delete',
      comment_id: commentId,
      comment_thread_id: res.threadId,
    });

    if (store.selectedThreadId === res.threadId) {
      store.selectedThreadId = undefined;
    }
  });

  const dialog = (
    <Dialog
      title="Delete this comment?"
      onClose={() => setIsConfirming(false)}
      buttons={
        <>
          <Button
            label="Cancel"
            onClick={() => setIsConfirming(false)}
            size="large"
          />
          <Button
            label="Delete"
            onClick={handleSubmit}
            loading={loading}
            variant="primary"
            background="negative"
            size="large"
          />
        </>
      }
    >
      <TextSmallLight color="grey">
        Are you sure you want to delete this comment?
      </TextSmallLight>
      <RedErrorText error={error} />
    </Dialog>
  );

  return [
    canDelete
      ? [
          menuItem({
            label: 'Delete comment',
            onClick: () => setIsConfirming(true),
          }),
        ]
      : [],
    isConfirming ? dialog : undefined,
    () => setIsConfirming(true),
  ] as const;
}

function ReplyRow({
  threadId,
  store,
}: {
  threadId: number;
  store: CommentingUiStore;
}) {
  const [text, setText] = React.useState('');
  const [showLogin, setShowLogin] = React.useState(false);

  const [loading, error, handleSubmit] = useSubmit(async () => {
    if (!text) throw new Error('Please enter a comment');
    await store.comments.submitComment(text, { type: 'reply', threadId });
    store.session.analytics.track({
      event: 'project:comment_create',
      editor_mode: editorModeStore.get(),
      comment_type: 'reply',
      comment_thread_id: threadId,
      comment_viewport: store.activeViewport,
    });
    setText('');
  });

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.stopPropagation();
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit],
  );
  const handleCancel = React.useCallback(() => {
    setText('');
  }, []);

  if (store.session.InlineAuthDialog) {
    return (
      <>
        <Button
          variant="primary"
          label="Reply"
          onClick={() => setShowLogin(true)}
        />
        {showLogin && (
          <store.session.InlineAuthDialog
            onClose={() => setShowLogin(false)}
            signupTitle="Create a free account to reply"
            isCommenter={true}
          />
        )}
      </>
    );
  } else {
    return (
      <Rows spacing={8}>
        <div className={styles.replyRow}>
          <Avatars avatars={[store.comments.myAvatar]} max={1} />
          <TextSmallLight>
            <Textarea
              allowNewlines={true}
              value={text}
              onChange={setText}
              placeholder="Reply"
              onKeyDown={handleKeyDown}
            />
          </TextSmallLight>
        </div>
        <RedErrorText error={error} />
        {text && (
          <Inline spacing={8}>
            <SpacerGrow />
            <Button label="Cancel" variant="secondary" onClick={handleCancel} />
            <Button
              label="Reply"
              variant="primary"
              onClick={handleSubmit}
              loading={loading}
            />
          </Inline>
        )}
      </Rows>
    );
  }
}
